import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Sparkles, Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { api } from "@/services/api";
import { getSupabase } from "@/lib/supabase";
import { wipeAnySession } from "@/lib/wipeSession";
import { resolvePostLoginRoute } from "@/lib/postLoginRoute";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Phase 7.46 — form controlado e autenticação real via Supabase Auth
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const enterSprints = async (e) => {
    e?.preventDefault?.();
    setLoginError(null);
    if (!email.trim() || !senha) {
      setLoginError("Informe e-mail e senha.");
      return;
    }
    setSubmitting(true);
    try {
      await signInAndRoute(email.trim(), senha);
    } catch (err) {
      setLoginError(err?.message || "Falha ao autenticar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Núcleo de sign-in Supabase + roteamento inteligente.
   * Usado pelo form manual E pelos botões de "Perfis de teste".
   */
  const signInAndRoute = async (userEmail, userPassword) => {
    // Phase 12.33 — WIPE TOTAL antes de qualquer sign-in.
    // Antes tínhamos apenas remoção de `imp_session` / `v360.empresaId`, mas o
    // `app_session` legado do usuário anterior (ex.: admin SpaceX) permanecia
    // até o `sessionBridge` reagir ao SIGNED_IN. Isso fazia com que na primeira
    // renderização do /app/dashboard o TenantContext ainda mostrasse o tenant
    // ERRADO (SpaceX aparecia por 1 frame antes de virar EDF).
    // Reusa o util compartilhado com SetupSenha para garantir consistência.
    await wipeAnySession();

    // 2) Sign-in via Supabase Auth
    const sb = await getSupabase();
    const { data, error } = await sb.auth.signInWithPassword({
      email: userEmail,
      password: userPassword,
    });
    if (error) {
      throw new Error(error.message === "Invalid login credentials"
        ? "E-mail ou senha inválidos."
        : error.message);
    }
    if (!data?.session) throw new Error("Sessão não emitida — tente novamente.");

    // Jun/2026 — senha provisória: antes de qualquer roteamento, o servidor
    // decide se este login precisa trocar a senha (flag em adm.usuarios).
    try {
      const me = await api.get("/auth/me");
      if (me?.senha_provisoria) {
        navigate("/trocar-senha", { replace: true });
        return;
      }
    } catch { /* se /me falhar, segue o fluxo normal */ }

    // 3) Bootstrap do app_session (via sessionBridge, que já escuta SIGNED_IN)
    // Aguarda o bridge escrever para não causar race no navigate.
    await new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (localStorage.getItem("app_session")) return resolve();
        if (Date.now() - start > 4000) return resolve(); // fallback
        setTimeout(tick, 60);
      };
      tick();
    });

    // 4) Roteamento por plano (defaults abertos por ora — sessionBridge sempre marca has_sprints=true)
    let session = null;
    try { session = JSON.parse(localStorage.getItem("app_session") || "null"); }
    catch { /* noop */ }

    toast.success(`Bem-vindo(a), ${(session?.user_nome || "").split(" ")[0] || "🎉"}!`);

    // Jun/2026 — Super-ADMIN da plataforma não tem workspace/empresa próprios.
    // Vai direto ao Console ADM (/admin); sem esta guarda ele caía em
    // /app/dashboard e via o tenant mock (Vector Logística).
    if (session?.is_super_adm) {
      navigate("/admin");
      return;
    }

    // Landing por módulo contratado (Fev/2026 — lib/postLoginRoute.js,
    // único lugar que decide isso; Login.jsx e SetupSenha.jsx chamam a
    // mesma função para nunca mais divergir):
    //   1. Primeiro acesso de verdade → /app/welcome.
    //   2. Tem os 2 módulos → Painel V360, salvo sem permissão de Gestão
    //      (acesso_gestao) → Dashboard de Projetos.
    //   3. Só Projetos → Dashboard (checando acesso_projetos).
    if (session?.empresa_perfil_id) {
      try { localStorage.setItem("v360.empresaId", session.empresa_perfil_id); } catch { /* noop */ }
    }
    const destino = await resolvePostLoginRoute(session);
    navigate(destino);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left – brand panel */}
      <div className="hidden lg:flex flex-col justify-between items-center text-center p-12 bg-slate-50 border-r border-slate-200 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate pointer-events-none" />
        <div className="relative flex items-center gap-3">
          {/* Jun/2026 — Marca Luoggo Gestão Integrada */}
          <img src="/luoggo-logo.png" alt="Luoggo" className="h-10 w-auto" data-testid="login-brand-logo" />
          <span className="font-display font-semibold tracking-tight text-blue-900 leading-none text-[45px]" data-testid="login-brand-text">
            Gestão Estratégica
          </span>
        </div>

        <div className="relative space-y-12 max-w-md py-8">
          <div className="inline-flex items-center gap-2 text-[16px] font-semibold uppercase tracking-[0.12em] text-blue-700">
            <span className="inline-flex items-center justify-center gap-1.5 text-blue-900 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1">
              <Sparkles className="w-3 h-3" /> Gestão da Empresa
            </span>
            <span className="text-blue-700 text-[26px]">+</span>
            <span className="inline-flex items-center justify-center gap-1.5 text-blue-900 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1">
              Gestão de Atividades
            </span>
          </div>
          <h1 className="font-display text-4xl font-medium tracking-tight text-slate-900 leading-[1.1]">
            Saiba onde está.<br/>
            Planeje onde chegar.<br/>
            Priorize o que é importante.<br/>
            Monitore os resultados.
          </h1>
          <p className="text-slate-600 text-[15px] leading-relaxed">
            Orquestre as ações estratégicas, táticas e operacionais de forma sincronizada e consistente, em um só lugar.
          </p>
        </div>

        <div className="relative text-[11px] text-slate-400">© 2026 Luoggo Gestão Integrada</div>
      </div>

      {/* Right – Auth + Persona picker */}
      <div className="flex items-center justify-center p-6 lg:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            {/* Jun/2026 — Marca Luoggo Gestão Integrada (mobile) */}
            <img src="/luoggo-logo.png" alt="Luoggo" className="h-6 w-auto" data-testid="login-brand-logo-mobile" />
            <span className="font-display font-semibold tracking-tight text-indigo-700 leading-none text-[27px]" data-testid="login-brand-text-mobile">
              Gestão Estratégica
            </span>
          </div>

          {/* Cabeçalho do form — texto em pt-BR, duplo acesso */}
          <div className="mb-5">
            <h2 className="font-display text-xl font-medium tracking-tight text-slate-900">Acesse sua Área de trabalho</h2>
          </div>
          <form className="space-y-3" onSubmit={enterSprints}>
            <div className="space-y-1">
              <Label htmlFor="email" className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider">{t("login.email")}</Label>
              <Input id="email" type="email"
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder={t("login.email_placeholder")} autoComplete="email"
                     disabled={submitting}
                     className="h-10 border-slate-200 focus-visible:ring-blue-600" data-testid="login-email" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="senha" className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider">{t("login.senha")}</Label>
              <div className="relative">
                <Input id="senha" type={showSenha ? "text" : "password"}
                       value={senha} onChange={(e) => setSenha(e.target.value)}
                       placeholder={t("login.sua_senha_placeholder")} autoComplete="current-password"
                       disabled={submitting}
                       className="h-10 border-slate-200 focus-visible:ring-blue-600 pr-10" data-testid="login-password" />
                <button
                  type="button"
                  onClick={() => setShowSenha(v => !v)}
                  disabled={submitting}
                  aria-label={showSenha ? t("login.ocultar_senha") : t("login.mostrar_senha")}
                  title={showSenha ? t("login.ocultar_senha") : t("login.mostrar_senha")}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700 focus:text-blue-700 focus:outline-none disabled:opacity-50"
                  data-testid="login-toggle-senha"
                  tabIndex={-1}
                >
                  {showSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={() => navigate("/esqueci-senha")}
                  className="text-[11px] text-blue-700 hover:text-blue-900 hover:underline font-medium"
                  data-testid="link-esqueci-senha"
                >
                  {t("login.esqueci_senha")}
                </button>
              </div>
            </div>
            {loginError && (
              <div className="flex items-start gap-1.5 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5"
                   role="alert" data-testid="login-error">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{loginError}</span>
              </div>
            )}
            <Button type="submit" disabled={submitting}
                    className="w-full h-10 bg-blue-700 hover:bg-blue-800 text-white gap-2 font-medium disabled:opacity-60"
                    data-testid="login-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {submitting ? t("login.entrando") : t("login.entrar")}
            </Button>
          </form>

          {/* Set/2026 — bloco "Perfis de teste · Demo" REMOVIDO junto com
              `lib/testPersonas.js`: o arquivo carregava as 7 senhas em texto claro e o
              bundle público as servia a qualquer visitante (verificado em
              /static/js/bundle.js). Senha em código-fonte não volta. As 7 contas também
              já não existem — foram apagadas na limpeza da Fase 4.1. */}

        </div>
      </div>
    </div>
  );
}
